// =============================================================================
// D4 — Grille Prospects (staging / quarantaine) — Hub Commercial
// =============================================================================
// Réf : docs/audits/PLAN-IMPL-D4.md, LOT 1. Aligné sur la grille de référence
// HubGrid.jsx (OHGrid standard : tri, filtres, recherche, pagination, sélection
// multiple, actions de ligne, clic-ligne → détail).
//
// GÉNÉRIQUE : colonnes dérivées du profil (OMV = CEE). La CONVERSION en lot
// arrive au LOT 2 (vrai geste de Solène). Le bouton "+ Prospect (test)" est un
// échafaudage de validation interne (vrai flux = formulaire public LOT 3).
// =============================================================================
import { useState, useEffect, useCallback, useMemo } from "react";
import { Inbox, Plus, Trash2, XCircle, Copy, Building2, Hash, Archive, ArchiveRestore } from "lucide-react";
import { OHGrid } from "../../components/ui/oh-grid";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
// Le formulaire prospect RÉUTILISE le composant du formulaire DOSSIER (mêmes champs,
// mêmes dropdowns config-list, même structure 2 parties) — pas de formulaire parallèle.
import OmvCeeEditor from "../../components/omv/OmvCeeEditor";
import ClientDuplicateDialog from "./dossier/ClientDuplicateDialog";

// Base API (retiré par erreur lors du refactor des imports e81a51769 → toutes les
// requêtes `${API}/…` levaient un ReferenceError capté en « Erreur réseau » + 0
// prospect, AUCUN appel réseau émis ; build OK car no-undef = simple warning).
const API = process.env.REACT_APP_BACKEND_URL;

// Libellés + couleurs de statut (retirés par erreur lors du refactor des imports
// e81a51769, comme `API` → StatutBadge levait un ReferenceError = PAGE BLANCHE dès
// qu'une ligne s'affichait, pire pour Solène/10 prospects).
const STATUT_LABELS = {
  nouveau: "Nouveau",
  en_revue: "En revue",
  converti: "Converti",
  rejete: "Rejeté",
  doublon: "Doublon",
};
const STATUT_COLOR = {
  nouveau: "blue", en_revue: "amber", converti: "green", rejete: "red", doublon: "orange",
};

// Libellé d'affichage de la SOURCE. Sources connues mappées (accents propres) ;
// pour une régie (source = son nom dérivé, ex "etincelle") → capitalisation par
// défaut → "Etincelle". "etincelle" explicitement accentué "Étincelle".
const SOURCE_LABELS = {
  manuel: "Manuel",
  import_ocr: "Import OCR",
  regie: "Régie",
  etincelle: "Étincelle",
  form_public_d4: "Formulaire web",
};
function sourceLabel(v) {
  if (!v) return "—";
  return SOURCE_LABELS[v] || (v.charAt(0).toUpperCase() + v.slice(1));
}


function formatDate(d) {
  if (!d) return "";
  let s = d;
  if (typeof s === "string" && !s.endsWith("Z") && !s.slice(-6).match(/[+-]\d\d:\d\d/)) s = `${s}Z`;
  const dt = new Date(s);
  return Number.isNaN(dt.getTime()) ? "" : dt.toLocaleDateString("fr-FR");
}

function StatutBadge({ value }) {
  const c = STATUT_COLOR[value] || "blue";
  return (
    <Badge style={{
      background: `var(--oh-stat-${c}-bg)`, color: `var(--oh-stat-${c}-text)`,
      border: `1px solid var(--oh-stat-${c}-border)`,
    }}>{STATUT_LABELS[value] || value}</Badge>
  );
}

export default function ProspectsStagingGrid({ tenantId }) {
  const [data, setData] = useState({ prospects: [], total: 0, counts_by_statut: {} });
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  // Rôle courant (depuis le user persisté) : la RÉGIE a une vue cloisonnée —
  // création + consultation de SES prospects, sans conversion ni cycle de vie
  // (le verrou réel est BACKEND : allowlist + filtre created_by). Solène = complet.
  const role = (() => { try { return (JSON.parse(localStorage.getItem("user") || "{}") || {}).role; } catch { return null; } })();
  const isRegie = role === "regie";
  // UNE seule vue formulaire, deux modes : create (vide) / edit (pré-rempli data{}).
  // Le rendu = OmvCeeEditor (le composant du formulaire DOSSIER).
  const [editor, setEditor] = useState({ open: false, mode: "create", prospect: null });
  // Dialogue « Créer / Utiliser le prospect » — ne s'ouvre QUE sur un vrai doublon
  // de FORMULAIRE (prospect même SIRET, monde prospect). cf. handleProspectSave.
  const [prospectDup, setProspectDup] = useState({ open: false, existing: null, pendingData: null });

  const openCreate = () => setEditor({ open: true, mode: "create", prospect: null });
  const openEdit = (prospect) => setEditor({ open: true, mode: "edit", prospect });
  const closeEditor = () => setEditor({ open: false, mode: "create", prospect: null });

  const headers = useCallback(() => {
    const tok = localStorage.getItem("session_token");
    return {
      "Content-Type": "application/json",
      "X-Tenant-ID": tenantId || "",
      ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
    };
  }, [tenantId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/api/metier/prospects${showArchived ? "?include_archived=true" : ""}`;
      const res = await fetch(url, { headers: headers(), credentials: "include" });
      if (res.ok) setData(await res.json());
      else toast.error("Erreur chargement prospects");
    } catch { toast.error("Erreur réseau"); }
    finally { setLoading(false); }
  }, [headers, showArchived]);

  useEffect(() => { load(); }, [load]);

  // Lignes : champs dérivés du data{} CEE du prospect (générique : le profil pilote
  // le schéma). On garde le prospect complet (_raw) pour le détail (clic ligne).
  const rows = useMemo(() => (data.prospects || []).map((p) => {
    const ent = (p.data && p.data.entreprise) || {};
    const bat = (p.data && p.data.batiment) || {};
    const cee = (p.data && p.data.cee) || {};
    return {
      id: p.id,
      beneficiaire: ent.raison_sociale || ent.siret || "—",
      site: bat.nom_etablissement || "—",
      cp: bat.cp_travaux || "—",
      ville: bat.ville_travaux || "—",
      operation: cee.operation_cee || cee.code_fiche || "—",
      source: p.source || "—",
      created_at: p.created_at,
      statut: p.statut,
      archived: !!p.archived,
      siret: p.siret || ent.siret || "",   // conservé pour la recherche
      _raw: p,
    };
  }), [data.prospects]);

  // 8 colonnes (ordre figé, demande JJ) :
  // Bénéficiaire | Site | CP | Ville | Opération | Source | Reçu le | Statut
  const columns = useMemo(() => [
    { key: "beneficiaire", label: "Bénéficiaire", sortable: true },
    { key: "site", label: "Site", sortable: true },
    { key: "cp", label: "CP", sortable: true },
    { key: "ville", label: "Ville", sortable: true },
    { key: "operation", label: "Opération", sortable: true },
    { key: "source", label: "Source", sortable: true, render: (v) => sourceLabel(v) },
    { key: "created_at", label: "Reçu le", sortable: true, render: (v) => formatDate(v) },
    { key: "statut", label: "Statut", sortable: true, render: (v) => <StatutBadge value={v} /> },
  ], []);

  const counts = data.counts_by_statut || {};
  const stats = useMemo(() => [
    { key: "all", label: "Total", value: data.total || 0, color: "blue", filter: null },
    { key: "nouveau", label: "Nouveaux", value: counts.nouveau || 0, color: "green", filter: (r) => r.statut === "nouveau" },
    { key: "converti", label: "Convertis", value: counts.converti || 0, color: "cyan", filter: (r) => r.statut === "converti" },
    { key: "doublon", label: "Doublons", value: counts.doublon || 0, color: "orange", filter: (r) => r.statut === "doublon" },
    { key: "rejete", label: "Rejetés", value: counts.rejete || 0, color: "red", filter: (r) => r.statut === "rejete" },
    { key: "archive", label: "Archivés", value: data.archived_count || 0, color: "amber", filter: (r) => r.archived },
  ], [data.total, data.archived_count, counts]);

  // Archivage = geste PAR DÉFAUT (réversible) ; la suppression dure reste séparée.
  const archiveProspect = async (id, archived) => {
    try {
      const res = await fetch(`${API}/api/metier/prospects/${id}/${archived ? "archiver" : "desarchiver"}`, {
        method: "POST", headers: headers(), credentials: "include",
      });
      if (res.ok) { toast.success(archived ? "Prospect archivé" : "Prospect désarchivé"); closeEditor(); load(); }
      else toast.error("Erreur archivage");
    } catch { toast.error("Erreur réseau"); }
  };

  const setStatut = async (id, statut) => {
    try {
      const res = await fetch(`${API}/api/metier/prospects/${id}`, {
        method: "PATCH", headers: headers(), credentials: "include",
        body: JSON.stringify({ statut }),
      });
      if (res.ok) { toast.success(`Statut → ${STATUT_LABELS[statut] || statut}`); closeEditor(); load(); }
      else toast.error("Erreur statut");
    } catch { toast.error("Erreur réseau"); }
  };

  const removeProspect = async (id) => {
    if (!window.confirm("Supprimer ce prospect ?")) return;
    try {
      const res = await fetch(`${API}/api/metier/prospects/${id}`, { method: "DELETE", headers: headers(), credentials: "include" });
      if (res.ok) { toast.success("Prospect supprimé"); closeEditor(); load(); }
      else toast.error("Erreur suppression");
    } catch { toast.error("Erreur réseau"); }
  };

  // Cycle de vie UI simplifié (retrait "en revue") : écarter le junk (doublon /
  // rejeter / supprimer). Le vrai geste = CONVERTIR (action groupée, LOT 2).
  // RÉGIE : aucune action de cycle de vie (consultation seule de ses saisies ;
  // ces endpoints sont de toute façon 403 côté backend pour role=regie).
  const rowActions = isRegie ? [] : [
    { label: "Marquer doublon", icon: Copy, onClick: (row) => setStatut(row.id, "doublon") },
    { label: "Rejeter", icon: XCircle, onClick: (row) => setStatut(row.id, "rejete") },
    // Geste PAR DÉFAUT = archiver (réversible). Désarchiver si déjà archivé.
    { label: "Archiver", icon: Archive, show: (row) => !row.archived, onClick: (row) => archiveProspect(row.id, true) },
    { label: "Désarchiver", icon: ArchiveRestore, show: (row) => row.archived, onClick: (row) => archiveProspect(row.id, false) },
    { label: "Supprimer (définitif)", icon: Trash2, className: "text-[var(--oh-error-text)]", onClick: (row) => removeProspect(row.id) },
  ];

  // Action groupée : conversion en lot (DOSSIER seul, pas de devis). Atomique par
  // item côté back ; rapport par item (converti / doublon SIRET rattaché / erreur)
  // + avertissements SIREN-soft. Le devis est généré ENSUITE manuellement.
  const convertSelection = async (ids) => {
    if (!ids || !ids.length) return;
    if (!window.confirm(`Convertir ${ids.length} prospect(s) en dossier(s) ? (sans devis — il sera généré ensuite)`)) return;
    try {
      const res = await fetch(`${API}/api/metier/prospects/convert`, {
        method: "POST", headers: headers(), credentials: "include",
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) { toast.error("Erreur conversion"); return; }
      const d = await res.json();
      const s = d.summary || {};
      const parts = [];
      if (s.converti) parts.push(`${s.converti} converti(s)`);
      if (s.rattaches) parts.push(`${s.rattaches} client(s) rattaché(s) (SIRET existant)`);
      if (s.deja_converti) parts.push(`${s.deja_converti} déjà converti(s)`);
      if (s.erreur) parts.push(`${s.erreur} erreur(s)`);
      (s.erreur ? toast.warning : toast.success)(parts.join(" · ") || "Conversion terminée");
      const warns = (d.results || []).filter((r) => r.siren_warning);
      if (warns.length) toast.info(`${warns.length} avertissement(s) SIREN : établissement(s) du même groupe déjà en base.`);
      load();
    } catch { toast.error("Erreur réseau"); }
  };

  // RÉGIE : pas de conversion (réservée à Solène ; 403 backend).
  const bulkActions = isRegie ? [] : [
    { label: "Convertir en dossiers", icon: Inbox, onClick: convertSelection },
  ];

  // Dédup NON bloquante à la saisie (régie OU Solène) : booléen + réf masquée via
  // l'endpoint dédié (pas de fuite client/dossier). Passé à OmvCeeEditor.
  const checkDuplicate = useCallback(async (digits) => {
    const d = (digits || "").replace(/\D/g, "");
    const qs = d.length === 14 ? `siret=${d}` : `siren=${d}`;
    try {
      const res = await fetch(`${API}/api/metier/prospects/check-duplicate?${qs}`, { headers: headers(), credentials: "include" });
      if (res.ok) return await res.json();
    } catch { /* non bloquant */ }
    return null;
  }, [headers]);

  // Soumission UNIFIÉE (callback `onSubmit` d'OmvCeeEditor) : reçoit le `body`
  // {entreprise,representant,batiment,technique,cee} construit par LE composant
  // dossier → data{} == payload dossier (conversion lossless). create → POST ;
  // edit → PATCH. Retourne un booléen de succès au composant.
  // Persistance réelle (POST création / PATCH édition). Renvoie un booléen succès.
  const persistProspect = async (data, mode, prospect, file = null) => {
    try {
      let res;
      if (mode === "edit" && prospect) {
        res = await fetch(`${API}/api/metier/prospects/${prospect.id}`, {
          method: "PATCH", headers: headers(), credentials: "include",
          body: JSON.stringify({ data }),
        });
      } else {
        // Source = "regie" pour le compte régie (sinon sa saisie ne rentrerait pas
        // dans son propre filtre de liste source=regie) ; "manuel" pour les internes.
        res = await fetch(`${API}/api/metier/prospects`, {
          method: "POST", headers: headers(), credentials: "include",
          body: JSON.stringify({ source: isRegie ? "regie" : "manuel", data }),
        });
      }
      if (res.ok) {
        // Attache le formulaire importé AU prospect (storage only, PAS Zoho — la
        // conversion poussera). Best-effort : un échec n'invalide pas la sauvegarde.
        if (file) {
          const saved = await res.json().catch(() => ({}));
          const prospectId = (mode === "edit" && prospect) ? prospect.id : saved?.prospect?.id;
          if (prospectId) {
            try {
              const tok = localStorage.getItem("session_token");
              const fd = new FormData();
              fd.append("file", file);
              await fetch(`${API}/api/metier/prospects/${prospectId}/attach-form`, {
                method: "POST", credentials: "include",
                // Pas de Content-Type : le navigateur pose le boundary multipart.
                headers: {
                  "X-Tenant-ID": tenantId || localStorage.getItem("current_tenant_slug") || "",
                  ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
                },
                body: fd,
              });
            } catch { /* attache best-effort — non bloquant */ }
          }
        }
        toast.success(mode === "edit" ? "Prospect mis à jour" : "Prospect créé");
        closeEditor();
        load();
        return true;
      }
      toast.error("Erreur enregistrement");
      return false;
    } catch { toast.error("Erreur réseau"); return false; }
  };

  // Fiche prospect (doc brut) → carte pour ClientDuplicateDialog (forme "dossier").
  const prospectToMatch = (p) => ({
    id: p.id,
    reference: p?.data?.entreprise?.raison_sociale || `Prospect ${String(p.id || "").slice(0, 8)}`,
    etablissement: p?.data?.batiment?.nom_etablissement || "",
    activite: p?.data?.entreprise?.secteur_activite || p.source || "",
    statut: STATUT_LABELS[p.statut] || p.statut || "nouveau",
    created_at: p.created_at,
    is_active: false,
  });

  // Soumission UNIFIÉE (callback `onSubmit` d'OmvCeeEditor). DÉDUP SÉPARÉE PAR
  // MONDE (règle JJ) : à la CRÉATION, si un PROSPECT de même SIRET existe déjà
  // (monde formulaire), on propose « Créer / Utiliser le prospect » (même
  // dialogue que le monde dossier). Un simple dossier/client existant N'EST PAS
  // un doublon de formulaire → aucun dialogue ici (juste l'info discrète inline).
  const handleProspectSave = async (body, file = null) => {
    const data = {};
    for (const k of ["entreprise", "representant", "batiment", "technique", "cee"]) data[k] = body[k] || {};
    if (data.entreprise.siret) data.entreprise.siret = String(data.entreprise.siret).replace(/\D/g, "");

    if (editor.mode !== "edit") {
      const siret = data.entreprise.siret || "";
      if (siret.length === 14) {
        const dup = await checkDuplicate(siret);
        // SIRET-exact uniquement : un match SIREN-soft = autre établissement du
        // même groupe, PAS un doublon de formulaire → on laisse créer.
        if (dup?.prospect?.exists && dup.prospect.match === "siret" && dup.prospect.id) {
          try {
            const r = await fetch(`${API}/api/metier/prospects/${encodeURIComponent(dup.prospect.id)}`,
              { headers: headers(), credentials: "include" });
            const existing = r.ok ? await r.json() : null;
            if (existing) {
              setProspectDup({ open: true, existing, pendingData: data });
              return false; // le dialogue prend le relais (pas d'erreur affichée)
            }
          } catch { /* fiche introuvable → on laisse la création se faire */ }
        }
      }
    }
    return persistProspect(data, editor.mode, editor.prospect, file);
  };

  return (
    <div className="space-y-4" data-testid="prospects-staging-grid">
      {/* Header aligné HubGrid : barre couleur + titre + badge total + action */}
      <div className="flex items-center gap-3">
        <div className="w-1 h-6 rounded-full" style={{ background: "var(--oh-stat-green-text)" }} />
        <h2 className="text-lg font-medium" style={{ color: "var(--oh-text)" }}>Prospects</h2>
        <Badge className="text-xs px-2 py-0.5"
          style={{ background: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)", border: "1px solid var(--oh-stat-green-border)" }}>
          {data.total || 0} prospect{(data.total || 0) > 1 ? "s" : ""}
        </Badge>
        <span className="text-xs hidden md:inline" style={{ color: "var(--oh-text-muted)" }}>
          Staging — à convertir en dossiers (conversion en lot = LOT 2)
        </span>
        {/* Bouton création TOUJOURS visible (échafaudage test ; le bouton OHGrid
            disparaît en mode sélection multiple → on garde celui-ci en repli sûr). */}
        <label className="ml-auto flex items-center gap-1.5 text-xs cursor-pointer" style={{ color: "var(--oh-text-muted)" }}>
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)}
            className="w-3.5 h-3.5 rounded" data-testid="prospects-show-archived" />
          Inclure archivés{data.archived_count ? ` (${data.archived_count})` : ""}
        </label>
        <Button size="sm" onClick={openCreate} data-testid="prospects-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Prospect (test)
        </Button>
      </div>

      <OHGrid
        data={rows}
        columns={columns}
        stats={stats}
        rowActions={rowActions}
        bulkActions={bulkActions}
        headerActions={[
          // Échafaudage de TEST (vrai flux = formulaire public LOT 3). Gatable.
          { label: "Prospect (test)", icon: Plus, onClick: openCreate },
        ]}
        selectable={true}
        pagination={true}
        pageSize={25}
        loading={loading}
        onRefresh={load}
        onRowClick={(row) => openEdit(row._raw)}
        searchPlaceholder="Rechercher par bénéficiaire, établissement, SIRET..."
        searchFilter={(row, q) =>
          (row.beneficiaire || "").toLowerCase().includes(q) ||
          (row.site || "").toLowerCase().includes(q) ||
          (row.siret || "").includes(q)
        }
        defaultSortKey="created_at"
        defaultSortDir="desc"
        emptyMessage="Aucun prospect — ils arriveront via le formulaire public (LOT 3)"
        testIdPrefix="prospects"
      />

      {/* FICHE FORMULAIRE = LE composant du formulaire DOSSIER (OmvCeeEditor) :
          mêmes champs, mêmes dropdowns config-list (secteurs/fiches/délégataires/
          type_batiment/type_vmc/zone), même structure 2 parties. 2 modes :
          create = vide ; edit (clic ligne) = pré-rempli depuis prospect.data{}.
          La sauvegarde est déléguée via onSubmit → POST/PATCH /prospects (le
          composant ne PATCHe PAS un dossier). data{} == payload dossier (lossless).
          key = remonte le composant à chaque ouverture (ré-init de son état interne). */}
      <Dialog open={editor.open} onOpenChange={(o) => { if (!o) closeEditor(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <Building2 className="w-5 h-5" style={{ color: "var(--oh-text-muted)" }} />
              {editor.mode === "edit"
                ? (editor.prospect?.data?.entreprise?.raison_sociale || "Prospect")
                : "Nouveau prospect — Formulaire CEE"}
              {editor.mode === "edit" && editor.prospect && <StatutBadge value={editor.prospect.statut} />}
            </DialogTitle>
          </DialogHeader>
          {editor.mode === "edit" && editor.prospect ? (
            <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
              <Hash className="w-3 h-3 inline opacity-60" /> SIRET {editor.prospect.siret || "—"}
              {editor.prospect.siren ? ` (SIREN ${editor.prospect.siren})` : ""}
              {" • "}source {editor.prospect.source} • reçu le {formatDate(editor.prospect.created_at)}. Champs éditables.
            </p>
          ) : (
            <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
              Même formulaire que la fiche dossier (mêmes listes). En production les
              prospects arriveront aussi via le formulaire web public (LOT 3).
            </p>
          )}

          {editor.open && (
            <OmvCeeEditor
              key={editor.mode === "edit" ? (editor.prospect?.id || "edit") : "create"}
              dossier={editor.mode === "edit" ? (editor.prospect?.data || {}) : {}}
              tenantId={tenantId}
              onSubmit={handleProspectSave}
              submitLabel={editor.mode === "edit" ? "Enregistrer le prospect" : "Créer le prospect"}
              checkDuplicate={checkDuplicate}
              readOnly={isRegie && editor.mode === "edit"}
            />
          )}

          <DialogFooter className="gap-2 flex-wrap">
            {/* Cycle de vie (écarter le junk). La sauvegarde = bouton interne du formulaire.
                Masqué pour la régie (consultation seule de ses saisies). */}
            {!isRegie && editor.mode === "edit" && editor.prospect && (
              <div className="flex items-center gap-2 mr-auto">
                <Button variant="ghost" size="sm" onClick={() => setStatut(editor.prospect.id, "doublon")}>Doublon</Button>
                <Button variant="ghost" size="sm" onClick={() => setStatut(editor.prospect.id, "rejete")}>Rejeter</Button>
                <Button variant="outline" size="sm" className="text-[var(--oh-error-text)]" onClick={() => removeProspect(editor.prospect.id)}>Supprimer</Button>
              </div>
            )}
            <Button variant="ghost" onClick={closeEditor}>{editor.mode === "edit" ? "Fermer" : "Annuler"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Doublon de FORMULAIRE (monde prospect) : même mécanisme « Créer / Utiliser »
          que le monde dossier, alimenté par le prospect existant. */}
      <ClientDuplicateDialog
        open={prospectDup.open}
        mode="dossier"
        entity="prospect"
        matches={prospectDup.existing ? [prospectToMatch(prospectDup.existing)] : []}
        title="Un prospect identique existe déjà"
        description="Une fiche prospect avec ce SIRET existe déjà pour ce compte. Créez une nouvelle fiche (homonymie / nouvelle demande) ou ouvrez l'existante."
        createLabel="Créer un nouveau prospect"
        useLabel="Utiliser le prospect existant"
        onCreateAnyway={() => {
          const d = prospectDup.pendingData;
          setProspectDup({ open: false, existing: null, pendingData: null });
          if (d) persistProspect(d, "create", null);
        }}
        onUseExisting={() => {
          const existing = prospectDup.existing;
          setProspectDup({ open: false, existing: null, pendingData: null });
          if (existing) openEdit(existing); // ouvre la fiche prospect existante
        }}
        onCancel={() => setProspectDup({ open: false, existing: null, pendingData: null })}
      />
    </div>
  );
}
